import { SelfieSegmentation } from '@mediapipe/selfie_segmentation'
import { vclog } from '../hooks/vc-log'

/**
 * One segmentation run per camera track, shared by every consumer.
 *
 * This exists because two `SelfieSegmentation` instances cannot run at once.
 * Each builds its own WASM + WebGL context, and when the background dialog
 * started a second one to preview the pending choice, the PUBLISHED pipeline
 * began receiving empty masks: `destination-in` then erased the person and
 * `destination-over` filled the frame, so the sender and everyone else saw a
 * flat rectangle of background with nobody in it. The dialog's own preview
 * looked perfect throughout, which is what made it confusing.
 *
 * So segmentation is a shared resource keyed on the track. The publish pipeline
 * and the dialog preview both subscribe; each composites the same mask against
 * its own background. Consumers still call `start()`/`stop()` as if they owned
 * it — the sharing happens here.
 *
 * Keyed on the TRACK, not globally: two different cameras genuinely need two
 * runs. It is the same camera being segmented twice that has to be avoided.
 */

export interface SegmentationFrame {
  image: CanvasImageSource
  segmentationMask: CanvasImageSource
}

type Subscriber = (frame: SegmentationFrame) => void

interface Source {
  video: HTMLVideoElement
  segmentation: SelfieSegmentation
  subscribers: Set<Subscriber>
  refCount: number
  rafId: number | null
  running: boolean
}

const sources = new Map<MediaStreamTrack, Source>()
/**
 * How long a run outlives its last consumer. The background dialog's warm-up
 * releases as the dialog closes on Apply, a moment before the publish pipeline
 * acquires — without this the run was torn down and MediaPipe loaded again
 * from scratch, which is most of the wait after Apply.
 */
const IDLE_GRACE_MS = 5000
const idleTimers = new Map<Source, ReturnType<typeof setTimeout>>()
/**
 * In-flight creations. `createSource` awaits the first camera frames, so two
 * consumers starting together would both find the map empty and each build an
 * instance — the precise thing this module exists to prevent. They wait on the
 * same promise instead.
 */
const pending = new Map<MediaStreamTrack, Promise<Source>>()

export interface SegmentationHandle {
  /** Called for every segmented frame. Returns an unsubscribe function. */
  subscribe: (cb: Subscriber) => () => void
  /** Drop this consumer's claim. The run stops once the last one lets go. */
  release: () => void
}

/**
 * Subscribe to segmentation for `track`, starting a run if none exists.
 *
 * Resolves once frames are actually flowing, so a caller can publish knowing
 * the first composited frame will contain a person rather than blank canvas.
 */
export async function acquireSegmentation(
  track: MediaStreamTrack,
): Promise<SegmentationHandle> {
  let source = sources.get(track)
  if (source) {
    const idle = idleTimers.get(source)
    if (idle) {
      clearTimeout(idle)
      idleTimers.delete(source)
    }
  }

  if (!source) {
    // An idle run lingering on another camera goes now, not when its grace
    // ends: two concurrent runs break each other's masks.
    for (const [other, run] of sources) {
      if (run.refCount <= 0) destroySource(other, run)
    }
    let creation = pending.get(track)
    if (creation) {
      // Someone else is already building it; join theirs.
      source = await creation
      source.refCount++
      vclog('producer', 'segmentation: joined the run being started', {
        consumers: source.refCount,
      })
    } else {
      if (sources.size > 0) {
        // Should not happen with one camera. Loud, because a second concurrent
        // run is what silently empties the first one's masks, and last time the
        // only symptom was a background with nobody in it.
        vclog('producer', 'segmentation: WARNING starting a second concurrent run', {
          existingRuns: sources.size,
        })
      }
      creation = createSource(track)
      pending.set(track, creation)
      try {
        source = await creation
        sources.set(track, source)
        vclog('producer', 'segmentation: started a run', { consumers: 1 })
      } finally {
        pending.delete(track)
      }
    }
  } else {
    source.refCount++
    vclog('producer', 'segmentation: joined the existing run', {
      consumers: source.refCount,
    })
  }

  const owned = source
  let released = false

  return {
    subscribe(cb) {
      owned.subscribers.add(cb)
      return () => owned.subscribers.delete(cb)
    },
    release() {
      // Guarded: a double release would tear down a run other consumers are
      // still using, which is the very failure this module exists to prevent.
      if (released) return
      released = true
      owned.refCount--
      if (owned.refCount <= 0 && !idleTimers.has(owned)) {
        idleTimers.set(
          owned,
          setTimeout(() => {
            idleTimers.delete(owned)
            if (owned.refCount <= 0 && sources.get(track) === owned) destroySource(track, owned)
          }, IDLE_GRACE_MS),
        )
      }
    },
  }
}

async function createSource(track: MediaStreamTrack): Promise<Source> {
  const video = document.createElement('video')
  video.playsInline = true
  video.muted = true
  video.autoplay = true
  video.srcObject = new MediaStream([track])
  // In the document at zero opacity, not display:none and not detached —
  // Chrome stops decoding frames for both, and MediaPipe then segments an
  // empty image and returns an empty mask.
  Object.assign(video.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '2px',
    height: '2px',
    opacity: '0',
    pointerEvents: 'none',
    zIndex: '-1',
  })
  document.body.appendChild(video)
  await video.play().catch(() => {})
  await waitForFrames(video)

  const segmentation = new SelfieSegmentation({
    locateFile: (file) =>
      `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1.1675465747/${file}`,
  })
  // `selfieMode` off: it mirrors the output, which is right for a self-preview
  // and wrong for what everyone else receives.
  segmentation.setOptions({ modelSelection: 1, selfieMode: false })

  const source: Source = {
    video,
    segmentation,
    subscribers: new Set(),
    refCount: 1,
    rafId: null,
    running: true,
  }

  segmentation.onResults((results) => {
    for (const cb of source.subscribers) {
      try {
        cb(results as SegmentationFrame)
      } catch {
        // One consumer's draw failing must not stop the others'.
      }
    }
  })

  const pump = async () => {
    if (!source.running) return
    try {
      // Dimensions rather than `readyState`: a hidden video can report a low
      // readyState while frames are available, and sending a 0x0 image is
      // exactly what produces an empty mask.
      if (source.video.videoWidth > 0 && source.video.videoHeight > 0) {
        await source.segmentation.send({ image: source.video })
      }
    } catch {
      // A dropped frame is not worth tearing the run down for.
    }
    if (source.running) source.rafId = window.requestAnimationFrame(() => void pump())
  }
  void pump()

  return source
}

function destroySource(track: MediaStreamTrack, source: Source): void {
  const idle = idleTimers.get(source)
  if (idle) clearTimeout(idle)
  idleTimers.delete(source)
  sources.delete(track)
  source.running = false
  source.subscribers.clear()
  if (source.rafId !== null) window.cancelAnimationFrame(source.rafId)
  try {
    source.segmentation.close?.()
  } catch {
    // ignore
  }
  // The track belongs to whoever opened the camera — detach, never stop.
  source.video.srcObject = null
  source.video.remove()
  vclog('producer', 'segmentation: stopped, no consumers left', {})
}

/** Some browsers report readyState before any frame exists. */
function waitForFrames(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= 2 && video.videoWidth > 0) return Promise.resolve()
  return new Promise((resolve) => {
    const settle = () => {
      video.removeEventListener('loadeddata', settle)
      video.removeEventListener('canplay', settle)
      resolve()
    }
    video.addEventListener('loadeddata', settle)
    video.addEventListener('canplay', settle)
  })
}

/**
 * The camera track of any run already in progress.
 *
 * Derived from live state rather than a separately-maintained variable, so it
 * cannot go stale — which a module-level registry can, via HMR in dev or via
 * StrictMode's double-invoked effects. A consumer that needs "the camera this
 * app is already segmenting" should ask here: joining an existing run is always
 * correct, and starting a second one never is.
 */
export function getSegmentedTrack(): MediaStreamTrack | null {
  for (const [track] of sources) {
    if (track.readyState === 'live') return track
  }
  return null
}
